import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adm, apiErr } from "../../../lib/adminApi";
import { TInput, TArea, Sel, Toggle, Btn, Card, MediaPicker, Badge, statusTone } from "../ui";
import VariantsEditor from "../VariantsEditor";
import ProductMultiPicker from "../ProductMultiPicker";
import { money } from "../../../lib/api";

const PUB = ["draft", "scheduled", "published", "archived"];
const TYPE_CODES = ["PHYSICAL_PRODUCT", "DIGITAL_ALBUM", "DIGITAL_SINGLE", "RINGTONE", "APPAREL", "BUNDLE", "COLLECTOR_SET"];
const DIGITAL = ["DIGITAL_ALBUM", "DIGITAL_SINGLE", "RINGTONE"];

const NEW_PRODUCT = {
  name: "", subtitle: "", lookupKey: "", slug: "", edition: "Standard", format: "", price: 0,
  currency: "usd", artwork: "", typeCode: "PHYSICAL_PRODUCT", productType: "other", dimension: null,
  description: "", story: "", sku: "", salePrice: null, variants: [], inventory: null,
  physicalDigital: "physical", preorder: false, releaseDateInfo: "", memberDiscountEligible: true,
  referralRewardEligible: true, fulfillmentInfo: "", fifthDimensionEligible: false,
  publicationStatus: "draft", activeForSale: false, isPlaceholder: false, categoryIds: [],
  qualifiesForDimension: null,
  featured: false, searchKeywords: "", bundleItems: [], individualSale: true, bundleEligible: true,
  shippingOverride: { enabled: false, amount: 0 }, weightGrams: null, dimensionsCm: null,
};

export default function ProductsTab() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const refresh = () => adm.list("products").then(setItems).catch(() => {});
  useEffect(() => { refresh(); adm.list("categories").then(setCategories).catch(() => {}); }, []);

  const set = (k, v) => setEditing((x) => ({ ...x, [k]: v }));
  const toggleCat = (id) => setEditing((x) => ({
    ...x,
    categoryIds: (x.categoryIds || []).includes(id)
      ? x.categoryIds.filter((c) => c !== id)
      : [...(x.categoryIds || []), id],
  }));
  const save = async () => {
    try {
      const { _id, id, source, createdAt, updatedAt, stripeProductId, stripePriceId, stripeSyncStatus, ...body } = editing;
      body.physicalDigital = DIGITAL.includes(body.typeCode) ? "digital" : body.physicalDigital || "physical";
      if (id) await adm.update("products", id, body);
      else await adm.create("products", body);
      toast.success("Product saved — Stripe stays in sync automatically.");
      setEditing(null); refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const sync = async (p) => {
    try { await adm.syncStripe(p.id); toast.success("Stripe synced."); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const destroy = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? Consider archiving instead.`)) return;
    try { await adm.remove("products", p.id); refresh(); } catch (e) { toast.error(apiErr(e)); }
  };
  const isBundle = editing && ["BUNDLE", "COLLECTOR_SET"].includes(editing.typeCode);
  const isPhysical = editing && !DIGITAL.includes(editing.typeCode);

  return (
    <div data-testid="store-products-tab">
      <div className="flex justify-end mb-5">
        <Btn onClick={() => setEditing(NEW_PRODUCT)} testid="product-new">NEW PRODUCT</Btn>
      </div>

      {editing && (
        <Card className="mb-8 grid md:grid-cols-3 gap-4">
          <TInput label="NAME" value={editing.name} onChange={(v) => set("name", v)} testid="product-name" />
          <TInput label="SUBTITLE" value={editing.subtitle} onChange={(v) => set("subtitle", v)} />
          <TInput label="LOOKUP KEY (STRIPE — UNIQUE)" value={editing.lookupKey} onChange={(v) => { set("lookupKey", v); set("slug", v); }} testid="product-lookup" />
          <TInput label="PRICE (CENTS)" value={editing.price} onChange={(v) => set("price", Number(v) || 0)} type="number" testid="product-price" />
          <TInput label="SKU / INTERNAL ID" value={editing.sku} onChange={(v) => set("sku", v)} testid="product-sku" />
          <Sel label="PRODUCT TYPE (CONTROLS BEHAVIOUR)" value={editing.typeCode || "PHYSICAL_PRODUCT"} onChange={(v) => set("typeCode", v)}
            options={TYPE_CODES.map((c) => ({ v: c, l: c.replace(/_/g, " ") }))} testid="product-typecode" />
          <TInput label="FORMAT" value={editing.format} onChange={(v) => set("format", v)} />
          <TInput label="EDITION" value={editing.edition} onChange={(v) => set("edition", v)} />
          <Sel label="DIMENSION" value={editing.dimension || ""} onChange={(v) => set("dimension", v || null)}
            options={[{ v: "", l: "—" }, ...["01", "02", "03", "04"].map((v) => ({ v, l: `DIMENSION ${v}` }))]} />
          <Sel label="DIM V QUALIFYING ALBUM (PHYSICAL ONLY)" value={editing.qualifiesForDimension || ""}
            onChange={(v) => set("qualifiesForDimension", v || null)}
            options={[{ v: "", l: "— NOT QUALIFYING —" }, ...["01", "02", "03", "04"].map((v) => ({ v, l: `GRANTS DIMENSION ${v}` }))]}
            testid="product-qualifies-dim" />
          <TInput label="INVENTORY (BLANK = UNTRACKED)" value={editing.inventory} onChange={(v) => set("inventory", v === "" ? null : Number(v))} type="number" />
          <TInput label="RELEASE / PREORDER DATE INFO" value={editing.releaseDateInfo} onChange={(v) => set("releaseDateInfo", v)} />
          <MediaPicker label="ARTWORK / PRODUCT IMAGE" value={editing.artwork} onChange={(v) => set("artwork", v)} mode="url" filterType="image" testid="product-artwork" />
          <Sel label="PUBLICATION" value={editing.publicationStatus} onChange={(v) => set("publicationStatus", v)} options={PUB.map((v) => ({ v, l: v.toUpperCase() }))} testid="product-pubstatus" />
          <TInput label="SEARCH KEYWORDS (INTERNAL)" value={editing.searchKeywords} onChange={(v) => set("searchKeywords", v)} />
          <TInput label="FULFILLMENT INFO" value={editing.fulfillmentInfo} onChange={(v) => set("fulfillmentInfo", v)} />

          <div className="md:col-span-3 border-t border-white/10 pt-3">
            <p className="font-mono text-[9px] tracking-[0.3em] text-gold mb-3">CATEGORIES — ORGANIZATION & DISCOVERY (A PRODUCT MAY BELONG TO SEVERAL)</p>
            <div className="flex flex-wrap gap-2.5" data-testid="product-categories">
              {categories.map((c) => (
                <button key={c.id} onClick={() => toggleCat(c.id)} data-testid={`product-cat-${c.slug}`}
                  className={`font-mono text-[9px] tracking-[0.2em] border px-4 py-2 transition-colors ${
                    (editing.categoryIds || []).includes(c.id) ? "border-gold text-gold bg-gold/10" : "border-white/15 text-white/45 hover:border-white/40"}`}>
                  {c.name.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <TArea label="DESCRIPTION" value={editing.description} onChange={(v) => set("description", v)} className="md:col-span-3" rows={2} />
          <TArea label="STORY (LONG-FORM, OPTIONAL)" value={editing.story} onChange={(v) => set("story", v)} className="md:col-span-3" rows={2} />

          {isBundle && (
            <div className="md:col-span-3 border-t border-white/10 pt-3 grid md:grid-cols-3 gap-4">
              <p className="font-mono text-[9px] tracking-[0.3em] text-gold md:col-span-3">BUNDLE — PICK INCLUDED PRODUCTS. BUNDLE PRICE IS INDEPENDENT OF THE SUM OF ITEMS.</p>
              <ProductMultiPicker label="INCLUDED PRODUCTS" value={editing.bundleItems || []}
                onChange={(v) => set("bundleItems", v)} field="id"
                filter={(p) => p.id !== editing.id && p.bundleEligible !== false} testid="product-bundle-items" />
            </div>
          )}

          {isPhysical && (
            <div className="md:col-span-3 border-t border-white/10 pt-3 grid grid-cols-2 md:grid-cols-4 gap-4">
              <p className="font-mono text-[9px] tracking-[0.3em] text-gold col-span-2 md:col-span-4">SHIPPING (PHYSICAL) — WEIGHT/DIMENSIONS SUPPORT FUTURE CARRIER RATES</p>
              <TInput label="WEIGHT (GRAMS)" value={editing.weightGrams} onChange={(v) => set("weightGrams", v === "" ? null : Number(v))} type="number" testid="product-weight" />
              <TInput label="PACKAGE L×W×H (CM)" value={editing.dimensionsCm} onChange={(v) => set("dimensionsCm", v || null)} placeholder="31×31×1" />
              <div className="flex items-end pb-1">
                <Toggle label="SHIPPING OVERRIDE" checked={!!editing.shippingOverride?.enabled}
                  onChange={(v) => set("shippingOverride", { ...(editing.shippingOverride || {}), enabled: v })} testid="product-ship-override" />
              </div>
              {editing.shippingOverride?.enabled && (
                <TInput label="OVERRIDE AMOUNT (¢ PER UNIT)" value={editing.shippingOverride?.amount}
                  onChange={(v) => set("shippingOverride", { ...(editing.shippingOverride || {}), amount: Number(v) || 0 })} type="number" />
              )}
            </div>
          )}

          <VariantsEditor value={editing.variants} onChange={(v) => set("variants", v)} />

          <div className="md:col-span-3 flex flex-wrap gap-6 border-t border-white/10 pt-4">
            <Toggle label="ACTIVE FOR SALE" checked={!!editing.activeForSale} onChange={(v) => set("activeForSale", v)} testid="product-active" />
            <Toggle label="FEATURED" checked={!!editing.featured} onChange={(v) => set("featured", v)} testid="product-featured" />
            <Toggle label="PREORDER" checked={!!editing.preorder} onChange={(v) => set("preorder", v)} />
            <Toggle label="INDIVIDUAL SALE" checked={editing.individualSale !== false} onChange={(v) => set("individualSale", v)} />
            <Toggle label="BUNDLE ELIGIBLE" checked={editing.bundleEligible !== false} onChange={(v) => set("bundleEligible", v)} />
            <Toggle label="MEMBER DISCOUNT ELIGIBLE" checked={!!editing.memberDiscountEligible} onChange={(v) => set("memberDiscountEligible", v)} />
            <Toggle label="PLACEHOLDER / UNCONFIRMED" checked={!!editing.isPlaceholder} onChange={(v) => set("isPlaceholder", v)} testid="product-placeholder" />
          </div>
          <div className="flex gap-3 md:col-span-3">
            <Btn onClick={save} testid="product-save">SAVE</Btn>
            <Btn variant="ghost" onClick={() => setEditing(null)}>CANCEL</Btn>
          </div>
        </Card>
      )}

      {items.map((p) => (
        <div key={p.id} className="flex items-center justify-between gap-4 border-t border-white/10 py-4 px-2" data-testid={`product-row-${p.id}`}>
          <div className="flex items-center gap-4 min-w-0">
            {p.artwork && <img src={p.artwork} alt="" className="w-10 h-10 object-cover" />}
            <div className="min-w-0">
              <p className="text-white/90 truncate">{p.name}</p>
              <p className="font-mono text-[9px] text-white/40 mt-0.5">
                {p.lookupKey} · {money(p.price, p.currency)} · {p.typeCode || "—"}
                {(p.variants || []).length > 0 && ` · ${p.variants.length} VARIANTS`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
            {p.featured && <Badge tone="gold">FEATURED</Badge>}
            {p.isPlaceholder && <Badge tone="red">PLACEHOLDER</Badge>}
            {p.activeForSale ? <Badge tone="green">ON SALE</Badge> : <Badge tone="dim">NOT FOR SALE</Badge>}
            <Badge tone={statusTone(p.publicationStatus)}>{p.publicationStatus}</Badge>
            <Btn variant="ghost" onClick={() => sync(p)} testid={`product-sync-${p.id}`}>SYNC</Btn>
            <Btn variant="ghost" onClick={() => setEditing(p)} testid={`product-edit-${p.id}`}>EDIT</Btn>
            <button onClick={() => destroy(p)} className="text-red-400/70 hover:text-red-400 text-xs px-1">✕</button>
          </div>
        </div>
      ))}
    </div>
  );
}
